import type { ReactElement, ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../lib/cn";

export interface TooltipProps {
  /** What the tooltip says. Keep it to a few words: it names, it does not explain. */
  content: ReactNode;
  /** A keyboard shortcut shown after the text, e.g. `"Ctrl Z"`. */
  shortcut?: string;
  side?: "top" | "right" | "bottom" | "left";
  /** The element described. It must accept a ref and pointer/focus handlers (a button, a link…). */
  children: ReactElement;
  className?: string;
}

/**
 * A short label shown on hover and on keyboard focus. For icon-only controls,
 * pair it with an `aria-label` on the control: the tooltip is for sighted
 * users, the label is what assistive technology reads.
 */
export function Tooltip({ content, shortcut, side = "bottom", children, className }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              "z-90 flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-primary shadow-popover",
              "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none",
              className,
            )}
          >
            {content}
            {shortcut ? (
              <kbd className="rounded border border-border bg-surface-elevated px-1 font-sans text-2xs font-medium text-muted">
                {shortcut}
              </kbd>
            ) : null}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
